import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { useState } from 'react';
import type { PropertyModules } from '../../../../../packages/entities/Property';
import { apiRawFetch } from '../../main/client';
import ModuleConfiguration from '../components/ModuleConfiguration';
import { usePropertySettings } from './PropertySettingsContext';

const ModulesSettingsPage = () => {
    const { property, refreshProperty } = usePropertySettings();
    const [modules, setModules] = useState<PropertyModules>(property.modules ?? {});
    const [saving, setSaving] = useState(false);
    const [successOpen, setSuccessOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleModulesChange = async (newModules: PropertyModules) => {
        setModules(newModules);

        // Auto-save on every change
        try {
            setSaving(true);
            setError(null);

            const response = await apiRawFetch(`api/properties/${property.id}`, 'POST', {
                modules: newModules,
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({})) as { error?: string };
                throw new Error(data.error ?? 'Failed to save');
            }

            await refreshProperty();
            setSuccessOpen(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
            // Revert to previous state on error
            setModules(property.modules ?? {});
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            {error && (
                <Alert
                    severity="error"
                    onClose={() => setError(null)}
                    sx={{
                        mb: 2,
                        borderRadius: 0,
                        border: '3px solid #ef4444',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        fontWeight: 600,
                        '& .MuiAlert-icon': {
                            color: '#ef4444',
                        },
                    }}
                >
                    {error}
                </Alert>
            )}
            <ModuleConfiguration
                modules={modules}
                onChange={handleModulesChange}
                disabled={saving}
                propertyCountry={property.country}
            />

            {/* Success Snackbar */}
            <Snackbar
                open={successOpen}
                autoHideDuration={2000}
                onClose={() => setSuccessOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSuccessOpen(false)}
                    severity="success"
                    sx={{
                        borderRadius: 0,
                        border: '2px solid #22c55e',
                        backgroundColor: 'rgba(34, 197, 94, 0.1)',
                        fontWeight: 600,
                    }}
                >
                    Module settings saved
                </Alert>
            </Snackbar>
        </>
    );
};

export default ModulesSettingsPage;
